import type { SlashCommand } from "./chat";

export interface SelectionInfo {
  text: string;
  /** True if more text exists beyond what's in `text` */
  truncated: boolean;
}

/**
 * Base adapter interface for any CAi plugin.
 * Each plugin provides tools, prompts, commands, and context methods.
 */
export interface AppAdapter {
  /** Session type for the CAi backend (e.g., "office_word", "office_excel") */
  sessionType: string;

  /** Display name for the app (shown in header). Defaults to "CAi". */
  appName?: string;

  /** App-specific tools for the agent (AI SDK tool record: { name: toolDef }) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tools: Record<string, any>;

  /** System prompt with app-specific API reference */
  systemPrompt: string;

  /** Quick actions shown in slash command menu */
  slashCommands: SlashCommand[];

  /** Get current context to prepend to messages (e.g., document info, email subject) */
  getContext?: () => Promise<string>;

  /** Get a display name for the current context (e.g., document title, workbook name) */
  getContextName?: () => Promise<string>;

  /** Get a unique ID for the current context (for session grouping) */
  getContextId?: () => Promise<string>;
}

/**
 * Extended adapter for Office Add-in plugins.
 * Adds Office.js-specific features like selection tracking and inline prompt markers.
 */
export interface OfficeAdapter extends AppAdapter {
  /** Subscribe to document selection changes. Returns unsubscribe function. */
  onSelectionChanged?: (
    callback: (selection: SelectionInfo | null) => void
  ) => () => void;

  /** Scan document for @@ prompt markers and return found prompts with their range context.
   *  Returns array of { prompt, context } or empty array. Removes the marker from the document. */
  processPromptMarkers?: () => Promise<
    { prompt: string; surroundingContext: string }[]
  >;
}

/** Type guard to check if an adapter has Office-specific capabilities */
export function isOfficeAdapter(
  adapter: AppAdapter
): adapter is OfficeAdapter {
  return (
    "onSelectionChanged" in adapter || "processPromptMarkers" in adapter
  );
}
